import { Button, Stack, IconButton, Typography } from '@mui/material';
import { AccessTime, Edit } from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import { FC, useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import clsx from 'clsx';
import { CustomTimeSelector } from '../../../../../common';

import './EditSleepModalView.scss';
import { selectedCoreActionModalAtom, selectedDateAtom } from '../../../../../../data-access';
import { BASIC_AWAKE_TIME, BASIC_SLEEP_TIME } from '../../../constants';
import {
  useEditSleep,
  useGeneralSettings,
  useOvernightSummary,
  useAllPlanSuggestedModal,
} from '../../../../../../hooks';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';

interface EditSleepModalViewProps {
  onClose?: () => void;
}

const EditSleepModalView: FC<EditSleepModalViewProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const selectedDate = useRecoilValue(selectedDateAtom);
  const [wakeTime, setWakeTime] = useState<Dayjs>(dayjs());
  const [sleepTime, setSleepTime] = useState<Dayjs>(dayjs());

  const { rerunAllSuggestions } = useAllPlanSuggestedModal();
  const { handleEditSleepData, isEditingData, canSubmitSleepEdit } = useEditSleep({
    date: selectedDate,
    onAfterEdit: rerunAllSuggestions,
  });
  const [_, setSelectedModal] = useRecoilState(selectedCoreActionModalAtom);
  const {
    awakeTime: defaultWakeTime,
    sleepTime: defaultSleepTime,
    hasNoSleepData,
  } = useOvernightSummary({
    date: selectedDate,
  });
  const {
    generalSettings: { timeFormat },
  } = useGeneralSettings();

  useEffect(() => {
    if (selectedDate) {
      setSleepTime(selectedDate.set('h', BASIC_SLEEP_TIME).set('m', 0).set('s', 0));
      setWakeTime(selectedDate.set('h', BASIC_AWAKE_TIME).set('m', 0).set('s', 0));
    }

    if (defaultWakeTime) {
      setWakeTime(dayjs(defaultWakeTime));
    }

    if (defaultSleepTime) {
      setSleepTime(dayjs(defaultSleepTime));
    }
  }, [defaultWakeTime, defaultSleepTime, selectedDate]);

  const canSave = useMemo(() => {
    const normalizedSleepTime = sleepTime.isBefore(wakeTime)
      ? sleepTime
      : sleepTime.subtract(1, 'day');

    if (!normalizedSleepTime.isBefore(wakeTime)) {
      return false;
    }

    if (hasNoSleepData) {
      return true;
    }

    const defaultSleep = dayjs(defaultSleepTime);
    const defaultWake = dayjs(defaultWakeTime);

    return (
      !normalizedSleepTime.isSame(defaultSleep, 'minute') || !wakeTime.isSame(defaultWake, 'minute')
    );
  }, [sleepTime, wakeTime, defaultSleepTime, defaultWakeTime, hasNoSleepData]);

  const handleSleepTimeEditClick = () => {
    setTimeout(() => {
      const inputElement = document.querySelector(
        '.edit-sleep-modal__time-input .MuiInputBase-input'
      ) as HTMLInputElement;
      if (inputElement) {
        inputElement.click();
        inputElement.focus();
      }
    }, 50);
  };

  const handleWakeTimeEditClick = () => {
    setTimeout(() => {
      const inputElements = document.querySelectorAll(
        '.edit-sleep-modal__time-input .MuiInputBase-input'
      );
      const wakeInputElement = inputElements[1] as HTMLInputElement;
      if (wakeInputElement) {
        wakeInputElement.click();
        wakeInputElement.focus();
      }
    }, 50);
  };

  const onSleepDataSave = async () => {
    if (!sleepTime || !wakeTime || !canSave) {
      return;
    }

    const normalizedSleepTime = sleepTime.isBefore(wakeTime)
      ? sleepTime
      : sleepTime.subtract(1, 'day');

    await handleEditSleepData({
      sleepTime: normalizedSleepTime.format('YYYY-MM-DDTHH:mm:ss'),
      wakeTime: wakeTime.format('YYYY-MM-DDTHH:mm:ss'),
    });
    setSelectedModal(null);
    onClose?.();
  };

  return (
    <div className="edit-sleep-modal">
      <div className="edit-sleep-modal__content">
        <h3>{t('task.coreActions.editSleep.title')}</h3>
        <div className="edit-sleep-modal__time-container">
          <div className="edit-sleep-modal__time-item">
            <Stack
              className="edit-sleep-modal__time-icon"
              direction="row"
              justifyContent="space-between"
            >
              <Stack direction="row" alignItems="center" gap={0.5}>
                <AccessTime fontSize="small" />
                <Typography className="edit-sleep-modal__time-label">
                  {t('task.common.starts')}
                </Typography>
              </Stack>
              <IconButton size="small" onClick={handleSleepTimeEditClick}>
                <Edit fontSize="small" className="edit-sleep-modal__time-icon-edit" />
              </IconButton>
            </Stack>
            <CustomTimeSelector
              value={sleepTime}
              onChange={(newValue) => {
                if (newValue && selectedDate) {
                  const newSleepTime = selectedDate
                    .set('hour', newValue.hour())
                    .set('minute', newValue.minute())
                    .set('second', 0)
                    .set('millisecond', 0);
                  setSleepTime(newSleepTime);
                }
              }}
              amPm={timeFormat === '12h'}
              fontWeight="bold"
              className="edit-sleep-modal__time-input"
            />
          </div>
          <div className="edit-sleep-modal__time-item">
            <Stack
              className="edit-sleep-modal__time-icon"
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack direction="row" alignItems="center" gap={0.5}>
                <AccessTime fontSize="small" />
                <Typography className="edit-sleep-modal__time-label">
                  {t('task.common.ends')}
                </Typography>
              </Stack>
              <IconButton size="small" onClick={handleWakeTimeEditClick}>
                <Edit fontSize="small" className="edit-sleep-modal__time-icon-edit" />
              </IconButton>
            </Stack>
            <CustomTimeSelector
              value={wakeTime}
              onChange={(newValue) => {
                if (newValue && selectedDate) {
                  const newWakeTime = selectedDate
                    .set('hour', newValue.hour())
                    .set('minute', newValue.minute())
                    .set('second', 0)
                    .set('millisecond', 0);
                  setWakeTime(newWakeTime);
                }
              }}
              amPm={timeFormat === '12h'}
              fontWeight="bold"
              className="edit-sleep-modal__time-input"
            />
          </div>
        </div>
        <div className="edit-sleep-modal__actions">
          <Button variant="text" onClick={onClose} className="edit-sleep-modal__cancel-button">
            {t('task.common.cancel')}
          </Button>
          <LoadingButton
            onClick={onSleepDataSave}
            disabled={isEditingData || !canSave || !canSubmitSleepEdit}
            variant="contained"
            className={clsx(
              'edit-sleep-modal__save-button',
              (!canSave || isEditingData || !canSubmitSleepEdit) &&
                'edit-sleep-modal__save-button--disabled'
            )}
            loading={isEditingData}
          >
            {!isEditingData && t('task.common.save')}
          </LoadingButton>
        </div>
      </div>
    </div>
  );
};

export default EditSleepModalView;
